/* eslint-disable */
import React, { useMemo } from "react";
import { useTable, useFilters, useSortBy } from "react-table";

// Reusable column text filter
function DefaultColumnFilter({ column: { filterValue, setFilter, placeholder } }) {
    return (
        <input
            value={filterValue || ""}
            onChange={(e) => setFilter(e.target.value || undefined)}
            placeholder={placeholder || "Search"}
            style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "10px 14px",
                border: "1px solid #e0e0e0",
                borderRadius: 6,
                outline: "none",
                fontSize: 14,
            }}
        />
    );
}

// "contains" text filter
function textFilterFn(rows, id, filterValue) {
    const q = String(filterValue || "").toLowerCase();
    return rows.filter((row) => String(row.values[id] ?? "").toLowerCase().includes(q));
}
textFilterFn.autoRemove = (val) => !val;

// Truncate helper (used by Description cell)
const truncate = (str, n = 75) => {
    if (!str) return "";
    return str.length > n ? str.slice(0, n) + "..." : str;
};

// Header checkbox with indeterminate state
function HeaderCheckbox({ checked, indeterminate, onChange, title }) {
    return (
        <input
            type="checkbox"
            checked={checked}
            ref={(el) => {
                if (el) el.indeterminate = indeterminate && !checked;
            }}
            onChange={(e) => onChange?.(e.target.checked)}
            title={title}
            style={{ cursor: "pointer" }}
        />
    );
}

/**
 * Props:
 * - data: Array<{ artifact_id, artifact_name, artifact_description, artifact_type }>
 * - selectedIds: string[]
 * - onChangeSelected: (newSelectedIds: string[]) => void
 */
export default function ArtifactTable({ data = [], selectedIds = [], onChangeSelected }) {
    // Current selection as a Set (fresh every render)
    const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

    // Columns depend on current selection so "checked" is never stale
    const columns = useMemo(
        () => [
            {
                Header: "Select",
                id: "select",
                disableSortBy: true,
                Filter: () => null,
                width: 64,
                Cell: ({ row }) => {
                    const id = row.original.artifact_id;
                    const isChecked = selectedSet.has(id);
                    return (
                        <div style={{ display: "flex", justifyContent: "center" }}>
                            <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) => {
                                    const next = new Set(selectedIds);
                                    if (e.target.checked) next.add(id);
                                    else next.delete(id);
                                    onChangeSelected && onChangeSelected(Array.from(next));
                                }}
                                aria-label={`Select ${row.original.artifact_name ?? row.original.artifact_id}`}
                                style={{ cursor: "pointer" }}
                            />
                        </div>
                    );
                },
            },
            {
                Header: "Name",
                accessor: "artifact_name",
                filter: "text",
                placeholder: "Search Name",
                Cell: ({ value }) => (
                    <div
                        title={String(value ?? "")}
                        style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                        {value}
                    </div>
                ),
            },
            {
                Header: "Description",
                accessor: "artifact_description",
                filter: "text",
                placeholder: "Search Description",
                Cell: ({ value }) => (
                    <div
                        title={String(value ?? "")}
                        style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                        {truncate(String(value ?? ""), 75)}
                    </div>
                ),
            },
            {
                Header: "Type",
                accessor: "artifact_type",
                filter: "text",
                placeholder: "Search Type",
                Cell: ({ value }) => (
                    <div
                        title={String(value ?? "")}
                        style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                        {value}
                    </div>
                ),
            },
        ],
        [selectedIds, selectedSet, onChangeSelected]
    );

    const defaultColumn = useMemo(() => ({ Filter: DefaultColumnFilter }), []);
    const filterTypes = useMemo(() => ({ text: textFilterFn }), []);

    const tableInstance = useTable(
        {
            columns,
            data,
            defaultColumn,
            filterTypes,
            autoResetFilters: false,
            autoResetSortBy: false,
            autoResetHiddenColumns: false, // avoid reset loop when columns change
        },
        useFilters,
        useSortBy
    );

    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = tableInstance;

    // Header "select all (filtered)" state — derived fresh each render
    const filteredIds = useMemo(() => rows.map((r) => r.original.artifact_id), [rows]);
    const numFiltered = filteredIds.length;
    const numSelectedInFiltered = useMemo(
        () => filteredIds.reduce((acc, id) => acc + (selectedSet.has(id) ? 1 : 0), 0),
        [filteredIds, selectedSet]
    );
    const allChecked = numFiltered > 0 && numSelectedInFiltered === numFiltered;
    const someChecked = numSelectedInFiltered > 0 && numSelectedInFiltered < numFiltered;

    return (
        <div
            style={{
                maxHeight: 420,
                overflow: "auto",
                borderRadius: 8,
                border: "1px solid #e6e6e6",
            }}
        >
            <table
                {...getTableProps()}
                style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}
            >
                <thead>
                    {headerGroups.map((headerGroup) => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => {
                                const isSelectCol = column.id === "select";
                                const headerProps = column.getHeaderProps(
                                    isSelectCol ? undefined : column.getSortByToggleProps?.()
                                );
                                return (
                                    <th
                                        {...headerProps}
                                        style={{
                                            position: "sticky",
                                            top: 0,
                                            zIndex: 1,
                                            background: "#fafafa",
                                            textAlign: "left",
                                            fontWeight: 600,
                                            fontSize: 14,
                                            color: "#23323a",
                                            padding: "12px 16px 6px",
                                            borderBottom: "1px solid #e6e6e6",
                                            minWidth: isSelectCol ? 64 : 160,
                                            width: column.width,
                                        }}
                                        title={column.canSort && !isSelectCol ? "Toggle SortBy" : undefined}
                                    >
                                        {isSelectCol ? (
                                            <div style={{ display: "flex", justifyContent: "center" }}>
                                                <HeaderCheckbox
                                                    checked={allChecked}
                                                    indeterminate={someChecked}
                                                    title="Select/Deselect all (filtered)"
                                                    onChange={(checked) => {
                                                        const next = new Set(selectedIds);
                                                        if (checked) filteredIds.forEach((id) => next.add(id));
                                                        else filteredIds.forEach((id) => next.delete(id));
                                                        onChangeSelected && onChangeSelected(Array.from(next));
                                                    }}
                                                />
                                            </div>
                                        ) : (
                                            column.render("Header")
                                        )}
                                        <div style={{ marginTop: 8 }}>
                                            {column.canFilter ? column.render("Filter") : null}
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    ))}
                </thead>

                <tbody {...getTableBodyProps()}>
                    {rows.map((row) => {
                        prepareRow(row);
                        // Stable, data-driven key
                        const stableKey =
                            (row.original && row.original.artifact_id) || row.id;
                        return (
                            <tr {...row.getRowProps({ key: stableKey })}>
                                {row.cells.map((cell) => (
                                    <td
                                        {...cell.getCellProps()}
                                        style={{
                                            padding: "12px 16px",
                                            verticalAlign: "middle",
                                            borderBottom: "1px solid #f5f5f5",
                                            fontSize: 16,
                                            color: "#0f1720",
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                        }}
                                    >
                                        {cell.render("Cell")}
                                    </td>
                                ))}
                            </tr>
                        );
                    })}

                    {rows.length === 0 && (
                        <tr>
                            <td colSpan={columns.length} style={{ padding: 24, color: "#60717a" }}>
                                No artifacts match your filters.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}
